import { useState } from 'react'
import { useParams } from 'wouter'

import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/Button'
import { AddTeamMember } from '@/components/AddTeamMember'
import { MemberItem } from '@/components/MemberItem'
import { Spinner } from '@/components/Spinner'
import { useTeamMembers } from '@/hooks/useTeamMembers'
import { useDeleteTeamMember } from '@/hooks/useDeleteTeamMember'
import { useToast } from '@/hooks/Toast'
import { useUser } from '@/hooks/useUser'

import s from './TeamMembers.module.css'

export const TeamMembers = () => {
  const { teamId } = useParams()
  const { 
    data: members, 
    isLoading, 
    isFetchingNextPage, 
    hasNextPage, 
    loadMoreRef 
  } = useTeamMembers(teamId)
  const { user } = useUser()
  const [showModal, setShowModal] = useState(false)
  const [removingMember, setRemovingMember] = useState<any | null>(null)
  const removeMemberMutation = useDeleteTeamMember()
  const toast = useToast()

  const handleRemove = async () => {
    try {
      await removeMemberMutation.mutateAsync({
        teamId: teamId!,
        memberId: removingMember.id,
      })
      setRemovingMember(null)
    } catch (error) {
      toast.error('Something went wrong. Try again?')
    }
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <div>
      <div className={s.header}>
        {teamId && (
          <Button
            variant="primary"
            onClick={(e) => {
              e.stopPropagation()
              setShowModal(true)
            }}
          >
            Add Members
          </Button>
        )}
      </div>
      <ErrorBoundary>
        {members.map((member) => (
          <MemberItem
            key={member.id}
            member={member}
            isMe={!!user?.userIds?.includes(member.userId)}
            onRemove={() => setRemovingMember(member)}
          />
        ))}
      </ErrorBoundary>
      {hasNextPage && (
        <div ref={loadMoreRef} style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
          {isFetchingNextPage && <Spinner />}
        </div>
      )}

      <Modal
        title="Add Members"
        description={`Choose members from your organization to add to the team.`}
        open={showModal}
        onClose={() => {
          setShowModal(false)
        }}
      >
        <AddTeamMember teamId={teamId!} onClose={() => setShowModal(false)} />
      </Modal>

      {removingMember && (
        <Modal
          title="Remove Member"
          description={`Are you sure you want to remove "${removingMember.name}" from the team?`}
          open={!!removingMember}
          onClose={() => setRemovingMember(null)}
        >
          <Button
            variant="destructive"
            disabled={removeMemberMutation.isPending}
            onClick={handleRemove}
          >
            Yes, Remove
          </Button>
        </Modal>
      )}
    </div>
  )
}
